import { reportError } from '@newo/error-reporting';
import axios, {
	type AxiosError,
	AxiosHeaders,
	type AxiosRequestConfig,
	type AxiosResponse,
	isAxiosError
} from 'axios';
import { jwtDecode } from 'jwt-decode';
import { createAuthHeaders } from './services/AuthService/auth-headers';
import type { AuthTokenPairResponse } from './services/AuthService/types';
import { STORAGE_KEYS } from './storageKeys';
import {
	type ApiClientConfig,
	type ApiResult,
	type ApiResultError,
	type RequestConfig,
	RequestTarget,
	type StorageAdapter,
	type TokenPair,
	type UtilRequestConfig
} from './types';

type ApiClientStateSnapshot = {
	config: ApiClientConfig | null;
	refreshPromise: Promise<string> | null;
	storage: StorageAdapter | null;
	tokenExpiresAt: number | null;
};

type ParseResponseOptions = {
	throwOnError?: boolean;
};

const REFRESH_LOCK_NAME = 'newo-api-client-token-refresh'; // Web Lock name, one per origin
const REFRESH_REQUEST_TIMEOUT = 30 * 1000; // one refresh POST — generous so a slow-but-successful refresh completes in one attempt
const REFRESH_RETRY_BASE_DELAY = 200; // base delay before a fail retry
const REFRESH_RETRY_JITTER = 200; // random spread so tabs don't retry in lockstep

class ApiClient {
	private readonly REFRESH_THRESHOLD = 60 * 1000;
	private config: ApiClientConfig | null = null;
	private storage: StorageAdapter | null = null;
	private refreshPromise: Promise<string> | null = null;
	private tokenExpiresAt: number | null = null;

	init(config: ApiClientConfig) {
		if (!config.apiHost) {
			throw new Error('Missing API Host');
		}
		this.config = config;
		this.storage = config.storage;
		this.refreshPromise = null;
		this.syncTokenExpiresAtFromStorage();
		this.config.onAuthStateChange?.(this.hasTokens());
	}

	captureState(): ApiClientStateSnapshot {
		return {
			config: this.config,
			refreshPromise: this.refreshPromise,
			storage: this.storage,
			tokenExpiresAt: this.tokenExpiresAt
		};
	}

	restoreState(state: ApiClientStateSnapshot): void {
		this.config = state.config;
		this.refreshPromise = state.refreshPromise;
		this.storage = state.storage;
		this.tokenExpiresAt = state.tokenExpiresAt;
	}

	private assertInitialized() {
		if (!this.config || !this.storage) {
			throw new Error('ApiClient not initialized');
		}
	}

	setTokens(tokens: TokenPair, expiresIn?: number | string) {
		this.assertInitialized();
		const storage = this.storage;

		if (!storage) {
			throw new Error('ApiClient storage not initialized');
		}

		storage.set(STORAGE_KEYS.accessToken, tokens.accessToken);
		storage.set(STORAGE_KEYS.refreshToken, tokens.refreshToken);
		this.tokenExpiresAt = this.resolveTokenExpiresAt(expiresIn);

		if (this.tokenExpiresAt !== null) {
			storage.set(STORAGE_KEYS.tokenExpiresAt, String(this.tokenExpiresAt));
		} else {
			storage.remove(STORAGE_KEYS.tokenExpiresAt);
		}

		this.config?.onAuthStateChange?.(true);
	}

	private resolveTokenExpiresAt(expiresIn?: number | string): number | null {
		if (expiresIn === undefined) {
			return null;
		}

		const seconds = Number(expiresIn);

		if (!Number.isFinite(seconds)) {
			reportError(new Error('ApiClient received a non-numeric expires_in'), {
				message: `expires_in=${expiresIn}`
			});
			return null;
		}

		return Date.now() + seconds * 1000;
	}

	clearTokens() {
		this.assertInitialized();
		const storage = this.storage;

		if (!storage) {
			throw new Error('ApiClient storage not initialized');
		}

		storage.remove(STORAGE_KEYS.accessToken);
		storage.remove(STORAGE_KEYS.refreshToken);
		storage.remove(STORAGE_KEYS.tokenExpiresAt);
		this.tokenExpiresAt = null;
		this.config?.onAuthStateChange?.(false);
	}

	getTokens(): TokenPair | null {
		this.assertInitialized();
		const storage = this.storage;

		if (!storage) {
			throw new Error('ApiClient storage not initialized');
		}

		const accessToken = storage.get(STORAGE_KEYS.accessToken);
		const refreshToken = storage.get(STORAGE_KEYS.refreshToken);

		if (!accessToken || !refreshToken) {
			return null;
		}

		return {
			accessToken,
			refreshToken
		};
	}

	getRemainingExpiresIn(): number | null {
		if (this.tokenExpiresAt === null) {
			return null;
		}

		return Math.max(0, Math.floor((this.tokenExpiresAt - Date.now()) / 1000));
	}

	isApiError(error: unknown): error is AxiosError {
		return isAxiosError(error);
	}

	parseResponse<T>(
		promise: Promise<T>,
		mapper?: undefined,
		options?: ParseResponseOptions
	): Promise<ApiResult<T>>;
	parseResponse<T, R>(
		promise: Promise<T>,
		mapper: (response: T) => R,
		options?: ParseResponseOptions
	): Promise<ApiResult<R>>;
	parseResponse<T, R>(
		promise: Promise<T>,
		mapper?: (response: T) => R,
		options?: ParseResponseOptions
	): Promise<ApiResult<T | R>> {
		return promise
			.then((data) => ({
				success: mapper ? mapper(data) : data
			}))
			.catch((error) => {
				const apiError = this.camelCasify(
					error?.response?.data
				) as ApiResultError;

				if (options?.throwOnError) {
					throw apiError;
				}

				return { error: apiError };
			});
	}

	toCamelCase(value: string): string {
		return value.replace(
			/[_-][a-z]/g,
			(match) => match[1]?.toUpperCase() ?? ''
		);
	}

	camelCasify(value: unknown): unknown {
		if (Array.isArray(value)) {
			return value.map((entry) => this.camelCasify(entry));
		}

		if (!value || typeof value !== 'object' || value instanceof Date) {
			return value;
		}

		return Object.entries(value).reduce<Record<string, unknown>>(
			(accumulator, [key, entry]) => {
				accumulator[this.toCamelCase(key)] = this.camelCasify(entry);
				return accumulator;
			},
			{}
		);
	}

	private getBaseUrl(target: RequestTarget = RequestTarget.Gateway): string {
		this.assertInitialized();
		const config = this.config;

		if (!config) {
			throw new Error('ApiClient config not initialized');
		}

		if (target === RequestTarget.Chat) {
			return `${config.chatApiHost}api/internal/v1/`;
		}

		return `${config.apiHost}api/`;
	}

	private isOrganizationToken(accessToken: string): boolean {
		try {
			return !!jwtDecode<{ customer_id: string }>(accessToken)?.customer_id;
		} catch (error) {
			reportError(error, { message: 'ApiClient token decode failed' });
			return false;
		}
	}

	private notifyOrganizationTokenExpires(accessToken?: string | null): boolean {
		if (
			accessToken &&
			this.isOrganizationToken(accessToken) &&
			this.config?.onOrganizationTokenExpires
		) {
			this.config?.onOrganizationTokenExpires?.();
			return true;
		}

		return false;
	}

	private hasTokens(): boolean {
		return this.getTokens() !== null;
	}

	private buildHeaders(
		headers?: AxiosRequestConfig['headers'],
		accessToken?: string | null
	) {
		const normalizedHeaders = AxiosHeaders.from({
			'Content-Type': 'application/json',
			Accept: 'application/json',
			...(headers ?? {})
		});

		if (accessToken && !normalizedHeaders.get('Authorization')) {
			normalizedHeaders.set(
				'Authorization',
				createAuthHeaders(accessToken).Authorization
			);
		}

		return normalizedHeaders;
	}

	private shouldRefreshProactively(): boolean {
		return (
			this.tokenExpiresAt !== null &&
			this.tokenExpiresAt - Date.now() < this.REFRESH_THRESHOLD
		);
	}

	private syncTokenExpiresAtFromStorage(): void {
		const storedExpiresAt = this.storage?.get(STORAGE_KEYS.tokenExpiresAt);
		const parsed =
			typeof storedExpiresAt === 'string'
				? Number(storedExpiresAt)
				: Number.NaN;
		this.tokenExpiresAt = Number.isFinite(parsed) ? parsed : null;
	}

	private handleUnauthorizedFailure(
		accessToken: string | null | undefined,
		config?: RequestConfig
	) {
		const organizationTokenExpirationHandled =
			this.notifyOrganizationTokenExpires(accessToken);
		this.clearTokens();
		if (organizationTokenExpirationHandled) {
			return;
		}

		if (config?.skipUnauthorizedHandler) {
			return;
		}

		this.config?.onUnauthorized?.();
	}

	private async refreshBeforeRequest(
		skipAuth: boolean,
		currentAccessToken?: string | null
	) {
		if (skipAuth) {
			return;
		}

		this.syncTokenExpiresAtFromStorage();

		if (!this.shouldRefreshProactively()) {
			return;
		}

		try {
			await this.refreshToken(currentAccessToken);
		} catch (error) {
			this.handleUnauthorizedFailure(currentAccessToken, {
				skipUnauthorizedHandler: skipAuth
			});
			throw error;
		}
	}

	private async executeRequest<T = unknown>(
		target: RequestTarget,
		requestConfig: Omit<
			RequestConfig,
			'target' | 'skipAuth' | 'returnFullResponse'
		>,
		accessToken: string | null | undefined,
		signal: AxiosRequestConfig['signal'],
		returnFullResponse: boolean
	): Promise<T> {
		const response = await axios({
			baseURL: this.getBaseUrl(target),
			...requestConfig,
			headers: this.buildHeaders(requestConfig.headers, accessToken),
			signal
		});

		return (returnFullResponse ? response : response.data) as T;
	}

	private notifyError(error: any, refreshable: boolean, config: RequestConfig) {
		if (!refreshable && !config.noErrorMessage && !config.silent) {
			this.config?.onError?.(
				error?.response?.status,
				error?.response?.data?.reason
			);
		}
	}

	private async refreshForRetry(
		accessToken: string | null | undefined,
		config: RequestConfig
	): Promise<string> {
		try {
			return await this.refreshToken(accessToken);
		} catch (refreshError) {
			this.handleUnauthorizedFailure(accessToken, config);
			throw refreshError;
		}
	}

	private buildRetryConfig(
		config: RequestConfig,
		accessToken: string
	): RequestConfig {
		const headers = AxiosHeaders.from(config.headers as AxiosHeaders);
		headers.set('Authorization', createAuthHeaders(accessToken).Authorization);

		return {
			...config,
			__retry: true,
			headers
		};
	}

	private isReportableRequestFailure(error: unknown): boolean {
		if (!isAxiosError(error) || axios.isCancel(error)) {
			return false;
		}

		const status = error.response?.status;

		return status === undefined || status >= 500;
	}

	private async handleRequestError<T = unknown>(
		error: any,
		config: RequestConfig,
		skipAuth: boolean,
		target: RequestTarget = RequestTarget.Gateway,
		currentAccessToken?: string | null
	): Promise<T> {
		const status = error?.response?.status;
		const refreshable =
			(status === 401 && target !== RequestTarget.Chat) || status === 403;

		if (skipAuth) {
			throw error;
		}

		this.notifyError(error, refreshable, config);

		if (!refreshable) {
			throw error;
		}

		if (config.__retry) {
			this.handleUnauthorizedFailure(currentAccessToken, config);
			throw error;
		}

		const newToken = await this.refreshForRetry(currentAccessToken, config);

		return this.request<T>(this.buildRetryConfig(config, newToken));
	}

	async request<T = unknown>(config: RequestConfig): Promise<T> {
		this.assertInitialized();

		try {
			return await this.requestWithRecovery<T>(config);
		} catch (error) {
			if (!config.__retry && this.isReportableRequestFailure(error)) {
				reportError(error, {
					message: `API request failed: ${config.errorType ?? `${config.method ?? 'GET'} ${config.url?.split('?')[0]}`}`
				});
			}

			throw error;
		}
	}

	private async requestWithRecovery<T = unknown>(
		config: RequestConfig
	): Promise<T> {
		const {
			target = RequestTarget.Gateway,
			skipAuth = false,
			returnFullResponse = false,
			...rest
		} = config;
		const { signal, ...requestConfig } = rest;
		const currentAccessToken = this.getTokens()?.accessToken;
		await this.refreshBeforeRequest(skipAuth, currentAccessToken);

		const tokens = this.getTokens();
		const accessToken = skipAuth ? null : tokens?.accessToken;

		try {
			return await this.executeRequest<T>(
				target,
				requestConfig,
				accessToken,
				signal,
				returnFullResponse
			);
		} catch (error: any) {
			return this.handleRequestError<T>(
				error,
				config,
				skipAuth,
				target,
				accessToken
			);
		}
	}

	get<T = unknown>(
		url: string,
		config?: Partial<UtilRequestConfig>
	): Promise<T> {
		return this.request<T>({
			...(config ?? {}),
			url,
			method: 'GET'
		});
	}

	post<T = unknown>(
		url: string,
		data?: unknown,
		config?: Partial<UtilRequestConfig>
	): Promise<T> {
		return this.request<T>({
			...(config ?? {}),
			url,
			data,
			method: 'POST'
		});
	}

	put<T = unknown>(
		url: string,
		data?: unknown,
		config?: Partial<UtilRequestConfig>
	): Promise<T> {
		return this.request<T>({
			...(config ?? {}),
			url,
			data,
			method: 'PUT'
		});
	}

	patch<T = unknown>(
		url: string,
		data?: unknown,
		config?: Partial<UtilRequestConfig>
	): Promise<T> {
		return this.request<T>({
			...(config ?? {}),
			url,
			data,
			method: 'PATCH'
		});
	}

	delete<T = unknown>(
		url: string,
		config?: Partial<UtilRequestConfig>
	): Promise<T> {
		return this.request<T>({
			...(config ?? {}),
			url,
			method: 'DELETE'
		});
	}

	private refreshToken(
		staleAccessToken: string | null | undefined
	): Promise<string> {
		this.assertInitialized();
		const storage = this.storage;

		if (!storage) {
			throw new Error('ApiClient storage not initialized');
		}

		if (!this.refreshPromise) {
			this.refreshPromise = this.refreshWithCrossTabLock(
				storage,
				staleAccessToken
			).finally(() => {
				this.refreshPromise = null;
			});
		}

		return this.refreshPromise;
	}

	private refreshWithCrossTabLock(
		storage: StorageAdapter,
		staleAccessToken: string | null | undefined
	): Promise<string> {
		const locks = globalThis.navigator?.locks;

		if (!locks) {
			return this.refreshUnlessRotatedElsewhere(storage, staleAccessToken);
		}

		return locks.request(REFRESH_LOCK_NAME, () =>
			this.refreshUnlessRotatedElsewhere(storage, staleAccessToken)
		);
	}

	private async refreshUnlessRotatedElsewhere(
		storage: StorageAdapter,
		staleAccessToken: string | null | undefined
	): Promise<string> {
		const refreshToken = storage.get(STORAGE_KEYS.refreshToken);

		if (!refreshToken) {
			throw new Error('No refresh token');
		}

		const accessToken = storage.get(STORAGE_KEYS.accessToken);

		if (
			staleAccessToken !== null &&
			staleAccessToken !== undefined &&
			accessToken &&
			accessToken !== staleAccessToken
		) {
			this.syncTokenExpiresAtFromStorage();
			return accessToken;
		}

		return await this.refreshAccessToken(storage, refreshToken);
	}

	private async refreshAccessToken(
		storage: StorageAdapter,
		refreshToken: string
	): Promise<string> {
		const response = await this.postTokenRefresh(refreshToken).catch(
			async (error) => {
				if (!this.isTransientRefreshError(error)) {
					throw error;
				}

				await this.waitBeforeRefreshRetry();

				return this.postTokenRefresh(
					storage.get(STORAGE_KEYS.refreshToken) ?? refreshToken
				);
			}
		);
		const { access_token, refresh_token, expires_in } = response.data;

		this.setTokens(
			{
				accessToken: access_token,
				refreshToken: refresh_token
			},
			expires_in
		);

		return access_token;
	}

	private waitBeforeRefreshRetry(): Promise<void> {
		const delay =
			REFRESH_RETRY_BASE_DELAY + Math.random() * REFRESH_RETRY_JITTER;

		return new Promise((resolve) => setTimeout(resolve, delay));
	}

	private postTokenRefresh(
		refreshToken: string
	): Promise<AxiosResponse<AuthTokenPairResponse>> {
		return axios.post<AuthTokenPairResponse>(
			`${this.getBaseUrl()}v1/auth/token/refresh`,
			{
				refresh_token: refreshToken
			},
			{ timeout: REFRESH_REQUEST_TIMEOUT }
		);
	}

	private isTransientRefreshError(error: unknown): boolean {
		if (!isAxiosError(error)) {
			return false;
		}

		if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
			return false;
		}

		const status = error.response?.status;

		return status === undefined || status >= 500;
	}
}

const apiClient = new ApiClient();

export { apiClient, type ApiClientStateSnapshot };
